import { saveAs } from 'file-saver';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { trackSegment } from '~/components/SegmentAnalytics';
import EcModal from '~/components/Shared/EcModal/EcModal';
import { showToast } from '~/components/Shared/EcToast/showToast';
import { FileMimeType } from '~/enums/FileMimeType';
import { HttpStatusCodeType } from '~/enums/HttpStatusCodeType';
import { useFormatDate } from '~/i18n/hooks/useFormatDate/useFormatDate';
import { useIntl } from 'react-intl';
import * as selectors from '~/redux/selectors';
import search from '~/redux/slices/search';
import { MODAL_EXCEL_EXPORT } from '~/types/modal.types';
import { ERROR, SUCCESS } from '~/types/toast.types';

const ExportSearchResults = ({
  onActionComplete,
  searchResult = {},
  onExport,
  exportResult,
}) => {
  const intl = useIntl();
  const formatDate = useFormatDate();
  const modal = useSelector(selectors.selectSearchResultModal);
  const dispatch = useDispatch();
  const hideModal = () => dispatch(search.actions.setModal());
  const pilotSearchQuery = useSelector(selectors.selectPilotSearchQuery);
  const { modalType } = modal || {};

  const onSuccess = (res) => {
    let excelMessage = intl.formatMessage({
      id: 'ExportSearchResults.exportStartMessage',
      defaultMessage: 'Your results have started exporting. ',
      description: 'Message when export starts',
    });
    if (res.status === HttpStatusCodeType.Ok) {
      showToast(SUCCESS, excelMessage);
      const blob = new Blob([res.data], { type: FileMimeType.Xlsx });
      const timestamp = formatDate(new Date(), 'utc');
      const filename = `${timestamp} analyzer.xlsx`;
      saveAs(blob, filename);
    } else if (res.status === HttpStatusCodeType.Accepted) {
      showToast(
        SUCCESS,
        excelMessage +
          intl.formatMessage({
            id: 'ExportSearchResults.emailNotificationMessage',
            defaultMessage:
              'You will receive an email when the export is complete.',
            description:
              'Message about email notification for export completion',
          }),
      );
    }
  };

  const onError = () => {
    showToast(
      ERROR,
      intl.formatMessage({
        id: 'ExportSearchResults.exportErrorMessage',
        defaultMessage:
          'Error exporting data, contact support@evisort for help.',
        description: 'Error message when export fails',
      }),
    );
  };

  const { data, isSuccess, isError } = exportResult;

  useEffect(() => {
    if (isSuccess) {
      onSuccess(data);
      onActionComplete();
      hideModal();
    }
    if (isError) {
      onError(data);
      onActionComplete();
      hideModal();
    }
  }, [data, isSuccess, isError]);

  if (modalType !== MODAL_EXCEL_EXPORT) return null;

  const handleExport = (exportSetting) => {
    const requestData = { query: pilotSearchQuery };

    trackSegment('Exporting excel', requestData);

    // according to the documentation for getTimezoneOffset(), pt (-7) would be 7 instead
    const offsetFromUTCInHour = -(new Date().getTimezoneOffset() / 60);
    const formData = exportSetting
      ? { ...requestData, offsetFromUTCInHour, exportSetting }
      : { ...requestData, offsetFromUTCInHour };

    onExport(formData);
  };

  const resultCount = searchResult.count;
  const totalDocsIncludingDups = searchResult.totalDocsIncludingDups;

  return (
    <EcModal
      modalType={modalType}
      width="800px"
      analyzerSearchQuery={pilotSearchQuery}
      title={intl.formatMessage({
        id: 'ExportSearchResults.modalTitle',
        defaultMessage: 'Search Result Excel Export Settings',
        description: 'Title for export settings modal',
      })}
      totalDocs={resultCount}
      totalDups={totalDocsIncludingDups}
      onExport={handleExport}
      hideModal={hideModal}
    />
  );
};

export default ExportSearchResults;
